import Vue from 'vue'
import uuid from 'uuid/v4'
import gql from 'graphql-tag'
import { apolloClient } from '@/vue-apollo'
import EventBus from '@/event-bus'

export const mutations = {
  setNodes (state, { nodes }) {
    Vue.set(state, 'nodes', nodes)
  },
  setTitle (state, { title }) {
    Vue.set(state, 'title', title)
  },
  setDescription (state, { description }) {
    Vue.set(state, 'description', description)
  },
  addNode (state, { node }) {
    node.id = uuid()
    Vue.set(state.nodes, node.id, node)
  },
  updateNode (state, { node }) {
    Vue.set(state.nodes, node.id, node)
  },
  removeNode (state, { node }) {
    // If the node is the active node unset activeNodeId
    if (state.activeNodeId === node.id) {
      state.activeNodeId = null
    }

    Vue.delete(state.nodes, node.id)
  },
  attachObject (state, { node, object }) {
    const updatedNode = Object.assign(node, {
      objectID: object.id
    })

    Vue.set(state.nodes, node.id, updatedNode)
    Vue.set(state.objectNodeMap, object.id, node.id)
  },
  setActiveNodeId (state, { activeNodeId }) {
    state.activeNodeId = activeNodeId
  },
  setSceneId (state, { sceneId }) {
    state.sceneId = sceneId
  },
  setPreviewImage (state, { previewImage }) {
    state.previewImage = previewImage
  }
}

function findRootObject (object) {
  if (!object) return
  if (object.userData.root) return object
  let currentObject = object
  while (currentObject && !currentObject.userData.root) {
    currentObject = currentObject.parent
  }

  return currentObject
}

export const actions = {
  selectObject ({ commit, state }, { intersects }) {
    const intersectedObjects = intersects.map(intersect => intersect.object)
    let rootObject = intersectedObjects.find(object => object.userData.root)
    if (!rootObject) {
      let index = 0
      rootObject = findRootObject(intersectedObjects[ index ])
      while (!rootObject && index < intersectedObjects.length) {
        rootObject = findRootObject(intersectedObjects[ ++index ])
      }
    }

    if (!rootObject) {
      commit('setActiveNodeId', { activeNodeId: null })
      return
    }

    const clickedNodeId = state.objectNodeMap[ rootObject.id ]
    if (typeof clickedNodeId !== 'undefined') {
      commit('setActiveNodeId', { activeNodeId: clickedNodeId })
    }
  },
  nodeObjectUpdated ({ commit, state }, { object }) {
    const nodeId = state.objectNodeMap[ object.id ]
    const node = Object.assign({}, state.nodes[nodeId])
    node.position = object.position.clone()
    node.scale = object.scale.clone()
    node.rotation = { x: object.rotation.x, y: object.rotation.y, z: object.rotation.z }
    commit('updateNode', { node })
  },
  async saveScene ({ commit, state, dispatch }) {
    await dispatch('getPreviewImage')

    const data = {
      title: state.title,
      nodes: state.nodes,
      preview: state.previewImage
    }

    return apolloClient.mutate({
      mutation: gql`mutation ($data: CreateSceneInput!) {
        createScene(data: $data) {
          id
          title
        }
      }`,
      variables: {
        data
      }
    })
      .then((res) => {
        commit('setSceneId', { sceneId: res.data.createScene.id })
        return res.data
      })
  },
  loadScene ({ commit }, { sceneId }) {
    return apolloClient.query({
      query: gql`query($sceneId: ID!) {
        scene(sceneId: $sceneId) {
          id
          nodes
          title
        }
      }`,
      variables: {
        sceneId: sceneId
      }
    })
      .then((res) => {
        commit('setNodes', { nodes: res.data.scene.nodes })
        commit('setSceneId', { sceneId: res.data.scene.id })
        commit('setTitle', { sceneId: res.data.scene.title })
        commit('setDescription', { sceneId: res.data.scene.description })
        return res
      })
  },
  /**
   * Gets a preview image from the renderer. This is a multistep process and is tricky as we
   * don't have direct access to the renderer from here, and getting and image is asynchronous
   */
  getPreviewImage ({ commit }) {
    EventBus.$emit('three-get-preview')
    return new Promise((resolve, reject) => {
      // After a timeout presume somehting has gone wrong
      const errorTimeout = setTimeout(() => {
        reject(new Error(`Couldn't get preview image.`))
      }, 1000 * 2)

      // Listen for the response for the renderer
      EventBus.$once('three-set-preview', previewImage => {
        clearTimeout(errorTimeout)
        commit('setPreviewImage', { previewImage })
        resolve(previewImage)
      })
    })
  }
}

export const getters = {
  activeNode (state) {
    return state.nodes[state.activeNodeId]
  }
}

export default {
  namespaced: true,
  state: {
    activeNodeId: null,
    objectNodeMap: {},
    sceneId: null,
    title: 'New Project',
    description: '',
    nodes: {
      3: {
        id: 3,
        type: 'PointLight',
        colour: '#fff',
        name: 'Point Light',
        intensity: 1,
        scale: {
          x: 1,
          y: 1,
          z: 1
        },
        position: {
          x: 1,
          y: 6,
          z: -5
        },
        rotation: {
          x: 0,
          y: 0,
          z: 0
        }
      },
      4: {
        id: 4,
        type: 'AmbientLight',
        colour: '#ffffff',
        intensity: 0.3
      }
    },
    previewImage: null
  },
  mutations,
  actions,
  getters
}
